// @ts-check
// Ventana emergente (modal) accesible reutilizable. Hasta ahora el proyecto
// evitaba deliberadamente los diálogos modales; se introduce a petición
// explícita para la confirmación de pista y el resultado de corrección de los
// paneles de intento (diagramas/pseudocódigo/python).
//
// Componente PURO (docs/03-frontend.md §4.2 "components no llaman fetch"): no
// importa `services/**`/`state/**`/`features/**`, no llama a `fetch` ni a
// `window.location`. La página/feature que lo monta rellena su cuerpo y define
// las acciones; este componente solo gestiona la presentación y la
// accesibilidad del modal (rol, foco atrapado, Escape, retorno de foco).
//
// Accesibilidad (WCAG 2.2, WAI-ARIA APG "Dialog (Modal)"):
//   - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` al título.
//   - Foco atrapado: Tab/Shift+Tab ciclan solo entre los elementos
//     enfocables del diálogo mientras está abierto.
//   - `Escape` cierra (salvo que se desactive con `cerrarConEscape: false`).
//   - Al abrir, el foco entra en el diálogo; al cerrar, VUELVE al elemento
//     que lo abrió (`opener`), nunca se pierde.
//   - El overlay tapa el fondo; hacer clic fuera del diálogo cierra (salvo
//     `cerrarAlClicFuera: false`).
import { createSafeElement, setSafeText } from '../security/safe-dom.js';
import { crearIcono } from './base/iconos.js';
import { EVENTO_DIALOGO_ABIERTO } from './base/eventos.js';
import './modal-dialog.css';

let contadorModales = 0;

/** Selector de elementos potencialmente enfocables dentro del diálogo. */
const SELECTOR_ENFOCABLES =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @typedef {Object} OpcionesModal
 * @property {string} titulo - Título del diálogo (nombre accesible, `aria-labelledby`).
 * @property {boolean} [cerrarConEscape] - Por defecto `true`. Si `false`, `Escape` no cierra.
 * @property {boolean} [cerrarAlClicFuera] - Por defecto `true`. Si `false`, clic en el overlay no cierra.
 * @property {boolean} [portal] - Por defecto `true`: al abrir, el overlay se mueve a `document.body` (inmune a `transform`/`overflow`/apilamiento de ancestros). `false` = se queda donde se haya insertado (útil cuando la página que lo usa no tiene esos problemas y quiere mantener el diálogo dentro de su propio árbol, p.ej. para tests con ámbito local).
 * @property {boolean} [devolverFoco] - Por defecto `true`: al cerrar, el foco vuelve al `opener`. `false` = el modal NO mueve el foco al cerrar (la página que lo usa gestiona el retorno de foco por su cuenta, p.ej. cuando devolverlo al opener dispararía por sí mismo una reapertura del modal).
 * @property {() => void} [onCerrar] - Se invoca tras cerrar (por Escape, clic fuera o botón de cerrar). No se invoca al llamar a `cerrar()` directamente.
 */

/**
 * Crea un modal accesible. Devuelve el `elemento` (overlay, oculto de
 * inicio), contenedores `cuerpo` (para el contenido) y `acciones` (para los
 * botones de pie), y métodos de control.
 * @param {OpcionesModal} opciones
 * @returns {{
 *   elemento: HTMLElement,
 *   cuerpo: HTMLElement,
 *   acciones: HTMLElement,
 *   fijarTitulo: (texto: string) => void,
 *   abrir: (opener?: HTMLElement | null) => void,
 *   cerrar: () => void,
 *   estaAbierto: () => boolean,
 *   destruir: () => void,
 * }}
 */
export function crearModalDialogo(opciones) {
  const {
    titulo,
    cerrarConEscape = true,
    cerrarAlClicFuera = true,
    portal = true,
    devolverFoco = true,
    onCerrar,
  } = opciones;

  contadorModales += 1;
  const idTitulo = `modal-dialog-title-${contadorModales}`;

  const overlay = createSafeElement('div', { className: 'modal-dialog-overlay' });
  overlay.hidden = true;

  const dialogo = createSafeElement('div', { className: 'modal-dialog' });
  dialogo.setAttribute('role', 'dialog');
  dialogo.setAttribute('aria-modal', 'true');
  dialogo.setAttribute('aria-labelledby', idTitulo);

  const cabecera = createSafeElement('div', { className: 'modal-dialog-header' });
  const tituloElemento = createSafeElement('h2', { className: 'modal-dialog-title', text: titulo });
  tituloElemento.id = idTitulo;

  // Aspa de trazo, no el glifo «✕» (rediseño, Fase 4a): el nombre accesible
  // lo da `aria-label`, el icono es decorativo.
  const botonCerrar = createSafeElement('button', { className: 'modal-dialog-close' });
  botonCerrar.append(crearIcono('cerrar'));
  botonCerrar.setAttribute('type', 'button');
  botonCerrar.setAttribute('aria-label', 'Cerrar');

  cabecera.append(tituloElemento, botonCerrar);

  const cuerpo = createSafeElement('div', { className: 'modal-dialog-body' });
  const acciones = createSafeElement('div', { className: 'modal-dialog-actions' });

  dialogo.append(cabecera, cuerpo, acciones);
  overlay.append(dialogo);

  /** @type {HTMLElement | null} */
  let openerElemento = null;
  let abierto = false;

  /** @returns {HTMLElement[]} */
  function elementosEnfocables() {
    // No se filtra por `offsetParent` (jsdom no calcula layout): se excluyen
    // los elementos con atributo `hidden` (propios o heredados), que es como
    // este panel oculta controles que no aplican en cada estado.
    return /** @type {HTMLElement[]} */ (
      Array.from(dialogo.querySelectorAll(SELECTOR_ENFOCABLES)).filter(
        (el) => el instanceof globalThis.HTMLElement && !el.hidden && el.closest('[hidden]') === null,
      )
    );
  }

  /** @param {KeyboardEvent} evento */
  function manejarKeydown(evento) {
    if (evento.key === 'Escape' && cerrarConEscape) {
      evento.stopPropagation();
      cerrarPorUsuario();
      return;
    }
    if (evento.key !== 'Tab') return;
    const enfocables = elementosEnfocables();
    if (enfocables.length === 0) {
      evento.preventDefault();
      dialogo.focus();
      return;
    }
    const primero = enfocables[0];
    const ultimo = enfocables[enfocables.length - 1];
    const activo = document.activeElement;
    if (evento.shiftKey && activo === primero) {
      evento.preventDefault();
      ultimo?.focus();
    } else if (!evento.shiftKey && activo === ultimo) {
      evento.preventDefault();
      primero?.focus();
    }
  }

  /** @param {MouseEvent} evento */
  function manejarClicOverlay(evento) {
    if (!cerrarAlClicFuera) return;
    if (evento.target === overlay) {
      cerrarPorUsuario();
    }
  }

  function cerrarPorUsuario() {
    cerrar();
    onCerrar?.();
  }

  /** @param {HTMLElement | null} [opener] */
  function abrir(opener) {
    if (abierto) return;
    openerElemento =
      opener ?? (document.activeElement instanceof globalThis.HTMLElement ? document.activeElement : null);
    // Portal a `document.body`: el overlay es `position: fixed`, pero un
    // ancestro con `transform`/`filter`/`contain`/`overflow` podría recortarlo
    // o crear un contexto de apilamiento que lo tape. Montarlo directamente en
    // `body` al abrir lo hace inmune a la maquetación de la página que lo usa
    // (patrón estándar de modales). Al cerrar se queda en `body` (oculto);
    // `destruir()` lo retira del DOM.
    if (portal && document.body && overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
    }
    overlay.hidden = false;
    abierto = true;
    // El listener vive en el propio diálogo (no en `document`): el foco está
    // atrapado dentro, así que los eventos de teclado burbujean hasta aquí, y
    // al retirar el elemento del DOM el listener se libera sin fugas.
    dialogo.addEventListener('keydown', manejarKeydown);
    overlay.addEventListener('click', manejarClicOverlay);
    // Foco inicial: primer enfocable del diálogo (o el propio diálogo si no
    // hubiera ninguno). `botonCerrar` siempre existe, así que en la práctica
    // el foco entra en un control real del diálogo.
    const enfocables = elementosEnfocables();
    if (enfocables.length > 0) {
      enfocables[0]?.focus();
    } else {
      dialogo.setAttribute('tabindex', '-1');
      dialogo.focus();
    }
    // Rediseño, Fase 6b: los momentos de recompensa (los XP que cuentan en
    // «Resultado de la corrección») se enganchan aquí, sin que el panel que
    // usa el diálogo sepa nada (`components/base/momentos.js`). Después del
    // foco: el momento nunca lo retrasa.
    dialogo.dispatchEvent(new globalThis.CustomEvent(EVENTO_DIALOGO_ABIERTO, { bubbles: true }));
  }

  function cerrar() {
    if (!abierto) return;
    abierto = false;
    overlay.hidden = true;
    dialogo.removeEventListener('keydown', manejarKeydown);
    overlay.removeEventListener('click', manejarClicOverlay);
    if (devolverFoco && openerElemento && typeof openerElemento.focus === 'function') {
      openerElemento.focus();
    }
    openerElemento = null;
  }

  botonCerrar.addEventListener('click', cerrarPorUsuario);

  return {
    elemento: overlay,
    cuerpo,
    acciones,
    /** @param {string} texto */
    fijarTitulo: (texto) => setSafeText(tituloElemento, texto),
    abrir,
    cerrar,
    estaAbierto: () => abierto,
    destruir: () => {
      cerrar();
      botonCerrar.removeEventListener('click', cerrarPorUsuario);
      overlay.remove();
    },
  };
}
